import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { Card } from '@shared/types';
import { useAssetUrl } from '../lib/useAssetUrl';
import {
  AGENT_COLORS,
  AGENT_LABELS,
  PRIORITY_META,
  WORK_TYPE_META,
  fmtDue,
  isImageAsset,
  previewAsset,
  receiptsLine,
  todayYmd,
} from '../lib/boardMeta';

/**
 * BoardCard — one card face on the Work Board (v2, Hologram-reference layout).
 * Pointer handling (drag vs. click-to-open) lives in the page; this face just
 * renders honestly: tag + priority chips ABOVE the title, agent dot + name,
 * work-type, due/ICE chips, comment count, Quinn's tick in Human review, a
 * live badge for flows, receipts on done cards, a thumbnail when the preview
 * asset is an image — and the ⋯ menu (Archive / Delete, Restore when
 * archived).
 */

function Thumb({ assetKey }: { assetKey: string }) {
  const url = useAssetUrl(assetKey);
  if (!url) return <div className="bcard-thumb" aria-hidden />;
  return <img className="bcard-thumb" src={url} alt="" loading="lazy" draggable={false} />;
}

/** The ⋯ menu. Its pointerdown never bubbles, so opening it can't start a drag. */
function CardMenu({
  card,
  onArchive,
  onRestore,
  onDelete,
}: {
  card: Card;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setConfirming(false);
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setConfirming(false);
  };

  return (
    <div
      className="cmenu"
      ref={ref}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="cmenu-btn"
        aria-label={`Options for: ${card.title}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
          setConfirming(false);
        }}
      >
        ⋯
      </button>
      {open && (
        <div className="cmenu-pop" role="menu">
          {card.archived_at ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                onRestore();
              }}
            >
              Restore to the board
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                onArchive();
              }}
            >
              Archive
            </button>
          )}
          {!confirming ? (
            <button type="button" role="menuitem" className="danger" onClick={() => setConfirming(true)}>
              Delete…
            </button>
          ) : (
            <div className="cmenu-confirm">
              <span>This is forever — the card and its whole conversation go.</span>
              <button
                type="button"
                role="menuitem"
                className="danger"
                onClick={() => {
                  close();
                  onDelete();
                }}
              >
                Yes, delete it
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export interface BoardCardProps {
  card: Card;
  /** Rendered inside the drag ghost — inert, no handlers, no tab stop. */
  ghost?: boolean;
  onPointerDown?: (e: ReactPointerEvent<HTMLDivElement>) => void;
  /** Keyboard path to the drawer (Enter / Space). */
  onOpen?: () => void;
  /** ⋯ menu actions — omit all three to hide the menu (e.g. the drag ghost). */
  onArchive?: () => void;
  onRestore?: () => void;
  onDelete?: () => void;
}

export function BoardCard({ card, ghost, onPointerDown, onOpen, onArchive, onRestore, onDelete }: BoardCardProps) {
  const wt = WORK_TYPE_META[card.work_type] ?? WORK_TYPE_META.other;
  const asset = previewAsset(card);
  const showThumb = asset !== null && isImageAsset(asset);
  const receipts = card.column_key === 'done' ? receiptsLine(card) : null;
  const overdue =
    !!card.due_date && card.due_date < todayYmd() && card.column_key !== 'done' && card.column_key !== 'scheduled_live';
  const needsYou = card.column_key === 'human_review' && !card.archived_at;
  const liveFlow = card.work_type === 'flow' && card.column_key === 'scheduled_live';
  const comments = card.comment_count ?? 0;
  const prio = card.priority ? PRIORITY_META[card.priority] : null;
  const tags = card.tags ?? [];
  const hasMenu = !ghost && !!(onArchive || onRestore || onDelete);

  return (
    <div
      className={`bcard${needsYou ? ' needs-you' : ''}${card.archived_at ? ' is-archived' : ''}`}
      data-card-id={ghost ? undefined : card.id}
      role={ghost ? undefined : 'button'}
      tabIndex={ghost ? -1 : 0}
      onPointerDown={ghost ? undefined : onPointerDown}
      onKeyDown={
        ghost || !onOpen
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }
      }
      aria-label={ghost ? undefined : `Open card: ${card.title}`}
    >
      {/* chips row ABOVE the title (reference layout): tags + priority */}
      {(tags.length > 0 || prio || card.archived_at) && (
        <div className="bcard-chips">
          {prio && (
            <span className={`pchip ${prio.cls}`} title={`${prio.label} priority`}>
              <span className="pglyph" aria-hidden>{prio.glyph}</span>
              {prio.label}
            </span>
          )}
          {tags.map((t) => (
            <span key={t.id} className="tchip" style={{ ['--tag' as string]: t.color }}>
              {t.name}
            </span>
          ))}
          {card.archived_at && <span className="bchip archived-chip">archived</span>}
        </div>
      )}

      <div className="bcard-title-row">
        <div className="bcard-title">{card.title}</div>
        {hasMenu && (
          <CardMenu
            card={card}
            onArchive={onArchive ?? (() => {})}
            onRestore={onRestore ?? (() => {})}
            onDelete={onDelete ?? (() => {})}
          />
        )}
      </div>

      <div className="bcard-top">
        <span className="adot" style={{ background: AGENT_COLORS[card.agent_id] ?? 'var(--ice)' }} aria-hidden />
        <span className="bcard-agent">{AGENT_LABELS[card.agent_id] ?? card.agent_id}</span>
        <span className="bcard-type" title={wt.label}>
          {wt.glyph} {wt.label}
        </span>
      </div>

      {showThumb && asset && <Thumb assetKey={asset} />}
      {!showThumb && asset && <div className="bcard-draft">draft attached — open to preview</div>}

      <div className="bcard-meta">
        {liveFlow && <span className="bchip live-badge">live</span>}
        {card.ice_score != null && (
          <span className="bchip ice-score" title="How big a win × how easy to run — the working-out is on the card">
            ICE {Number(card.ice_score.toFixed(1))}
          </span>
        )}
        {card.due_date && (
          <span className={`bchip${overdue ? ' overdue' : ''}`}>
            {overdue ? 'was due ' : 'due '}
            {fmtDue(card.due_date)}
          </span>
        )}
        {needsYou && <span className="bchip pass">Quinn passed ✓</span>}
        {comments > 0 && (
          <span className="bchip">
            {comments} {comments === 1 ? 'note' : 'notes'}
          </span>
        )}
      </div>

      {receipts && (
        <div className="bcard-receipt" title={receipts}>
          {receipts}
        </div>
      )}
    </div>
  );
}
